import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Building2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";

const EMPTY = {
  name: "", tax_id: "", contact_person: "", phone: "", address: "", email: "", location: "",
};

export function ClientFormFields({ form, set, prefix }) {
  const fields = [
    ["name", "Nombre / Empresa *", "text"],
    ["tax_id", "RUC / Cédula", "text"],
    ["contact_person", "Persona de contacto", "text"],
    ["phone", "Teléfono", "tel"],
    ["email", "Correo electrónico", "email"],
    ["location", "Ubicación", "text"],
    ["address", "Dirección", "text"],
  ];
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      {fields.map(([key, label, type]) => (
        <div key={key} className={`space-y-1.5 ${key === "address" ? "sm:col-span-2" : ""}`}>
          <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">{label}</Label>
          <Input
            type={type}
            value={form[key]}
            onChange={(e) => set((f) => ({ ...f, [key]: e.target.value }))}
            data-testid={`${prefix}-${key}-input`}
          />
        </div>
      ))}
    </div>
  );
}

export default function Clients() {
  const navigate = useNavigate();
  const [clients, setClients] = useState(null);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/clients", { params: search ? { search } : {} });
    setClients(data);
  }, [search]);

  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("El nombre del cliente es requerido");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/clients", form);
      toast.success(`Cliente "${data.name}" registrado`);
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(apiError(err, "No se pudo registrar el cliente"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="clients-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative min-w-[220px] flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Buscar por nombre, RUC o contacto..."
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="client-search-input"
          />
        </div>
        <Button onClick={() => setOpen(true)} className="gap-2 bg-primary hover:bg-[#0353E9]" data-testid="new-client-button">
          <Plus className="h-4 w-4" /> Nuevo Cliente
        </Button>
      </div>

      {clients === null ? (
        <div className="space-y-2">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-lg" />)}
        </div>
      ) : clients.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="clients-empty">
          <Building2 className="mx-auto h-8 w-8 text-slate-300" />
          <p className="mt-2 font-heading text-lg font-semibold text-slate-700">No hay clientes registrados</p>
          <p className="mt-1 text-sm text-slate-500">Registre su primer cliente para comenzar a crear órdenes.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs uppercase tracking-wider">Cliente</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">RUC / Cédula</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Contacto</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Teléfono</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Correo</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Órdenes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clients.map((c) => (
                  <TableRow
                    key={c.id}
                    className="cursor-pointer transition-colors duration-200 hover:bg-slate-50"
                    onClick={() => navigate(`/clients/${c.id}`)}
                    data-testid={`client-row-${c.id}`}
                  >
                    <TableCell className="text-sm font-semibold text-slate-900">{c.name}</TableCell>
                    <TableCell className="font-mono text-sm text-slate-600">{c.tax_id || "-"}</TableCell>
                    <TableCell className="text-sm text-slate-600">{c.contact_person || "-"}</TableCell>
                    <TableCell className="text-sm text-slate-600">{c.phone || "-"}</TableCell>
                    <TableCell className="text-sm text-slate-600">{c.email || "-"}</TableCell>
                    <TableCell className="text-right">
                      <span className="inline-flex h-6 min-w-[24px] items-center justify-center rounded-full bg-blue-50 px-2 text-xs font-semibold text-blue-700" data-testid={`client-order-count-${c.id}`}>
                        {c.order_count}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Nuevo Cliente</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-5" data-testid="client-form">
            <ClientFormFields form={form} set={setForm} prefix="client" />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="client-form-cancel">
                Cancelar
              </Button>
              <Button type="submit" disabled={saving} className="bg-primary hover:bg-[#0353E9]" data-testid="client-form-submit">
                {saving ? "Guardando..." : "Registrar cliente"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
